








































import {
  Vue,
  Component,
  Prop,
  Watch
} from "@/shared/vuepack";
import "@bend-financial/okta-signin-widget/dist/css/okta-sign-in.min.css";
import { Location } from "vue-router";
import _ from "lodash";
import { AuthType, FetchState, LoginMethod, UserServiceAccountType } from "../types";
import {
  LoginParam,
  useUserStore
} from "../store/user";
import PartnerFooter from "@/shared/components/PartnerFooter.vue";
import SupportCard from "@/shared/components/SupportCard.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import {
  OktaCredential,
  AccountsSummary,
  Home,
  HsabAccountSelection,
  HsabNotionalDashboard,
  BiometricAuthSettings
} from "@/employee/route-names";
import OktaSignIn from "@bend-financial/okta-signin-widget";
import oktaConfig from "@/okta-config";
import { OktaAuth } from "@/shared/api/auth";
import { oktaAccountExists, mobileAppAccessCheck } from "@/shared/api/userService";
import { Config } from "@/shared/config";
import { login as oktaLoginResource } from "@/shared/oktaResources";
import {
  ERROR_MESSAGE_BIOMETRIC_LOGIN_FAILED,
  useBiometricAuthStore
} from "../store/biometricAuth";
import * as datadog from "@/shared/datadog";
import { isMobileApp } from "@/shared/mobileHelper";
import { useThemeStore } from "@/shared/store/theme";
import { usePortalCustomizationStore } from "@/shared/store/portalCustomization";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useCredentialFlowStore } from "@/employee/store/credentialFlow";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";
import { useAppUpdatePromptStore } from "@/shared/appUpdatePrompt";
import Fdic from "@/shared/components/Fdic.vue";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { BankCustodian } from "@/admin/types";
import { handleError as handleApiError } from "@/shared/storeHelper";
import { usePartnerContentStore } from "@/shared/store/partnerContent";
import { ANALYTICS_EVENT } from "@/shared/constants/analyticsEvent";

const HsabPartnerId = 15;
const pause = delayMs => new Promise(resolve => setTimeout(resolve, delayMs));

const oktaErrorCodesToExcludeFromLog = [
  "E0000004", // Authentication error
  "E0000014", // Update credentials failed error
  "E0000069", // User locked error
  "E0000011" // Invalid token provided. This is handled by sign-in widget.
];

const jwtErrors = [
  "The JWT expired and is no longer valid",
  "The JWT was issued in the future"
];

@Component({
  components: {
    GenericLoadingSpinner,
    PartnerFooter,
    SupportCard,
    Fdic
  }
})
export default class HsabLogin extends Vue {

  notifierStore = useNotifierStore();
  themeStore = useThemeStore();

  userStore = useUserStore();
  portalCustomizationStore = usePortalCustomizationStore();
  hsabAccountStore = useHsabAccountsStore();
  credentialFlowStore = useCredentialFlowStore();
  biometricAuthStore = useBiometricAuthStore();
  featureFlagStore = useFeatureFlagStore();
  appUpdatePromptStore = useAppUpdatePromptStore();
  custodianSettingsStore = useCustodianSettingsStore();
  partnerContentStore = usePartnerContentStore();

  widget: OktaSignIn = null;
  loginParam: LoginParam = { username: "", logoutOnStart: false };
  showPartnerFooter = false;
  isSubmitting = false;
  isWidgetLoginInProcess = false;
  biometricAuthCreds = { username: undefined, password: undefined };
  redirectToBiometricsSetup = false;
  boundButtons = new WeakSet();

  @Prop()
  routeToForgotPassword: Location;

  @Prop()
  routeToForgotUsername: Location;

  @Prop()
  routeToNewUser: Location;

  get imgSrc () {
    if (!this.portalCustomizationStore.isPartnerCustomLogo) {
      return require("../../assets/hsab-logo.png");
    }

    return this.portalCustomizationStore.partnerLogoSrc;
  }

  get forgotPasswordLink () {
    return this.$router.resolve(this.routeToForgotPassword).href;
  }

  get forgotUsernameLink () {
    return this.$router.resolve(this.routeToForgotUsername).href;
  }

  get newUserLink () {
    return this.$router.resolve(this.routeToNewUser).href;
  }

  get isUserLoggedIn () {
    // Account type is only set once the user is logged in and exists in the user service
    return this.userStore.accountType && this.userStore.accountType !== UserServiceAccountType.None;
  }

  pushNonOktaUserToReAuthenticate () {
    return oktaAccountExists(this.loginParam.username)
      .then(resp => {
        if (resp.data.requiresReAuthentication) {
          this.credentialFlowStore.setCredentialFlowUsername(this.loginParam.username);
          this.$router.push({ name: OktaCredential });
        }
      })
      .catch(error => {
        window.location.href = "#";
        handleApiError({ error, rethrow: false });
      });
  }

  @Watch("portalCustomizationStore.partnerLogoSrc")
  rerenderWithLogo () {
    if (!this.isUserLoggedIn) {
      this.renderOktaWidget();
    }
  }

  @Watch("$route.query.partner")
  getCustomizationsForPartnerChange () {
    const partnerId = (this.$route.query.partner || this.portalCustomizationStore.partnerId);
    const parsedPartnerId = _.toSafeInteger(partnerId) || HsabPartnerId;

    if (parsedPartnerId) {
      this.portalCustomizationStore.getPartnerCustomLogo(parsedPartnerId);
    }

    this.themeStore.getTheme({ partnerId: parsedPartnerId, employerId: 0, forceUpdate: true });
    this.renderOktaWidget();
  }

  created () {
    this.custodianSettingsStore.loadDefaultUserCustodianSettings(BankCustodian.HsaBank);
  }

  mounted () {
    this.subscribeToDeviceEvents();
    this.initialize();
  }

  subscribeToDeviceEvents () {
    if (isMobileApp()) {
      document.addEventListener("deviceready", this.initializeForMobile, true);
      document.addEventListener("resume", this.onMobileDeviceResume, true);
    }
  }

  onMobileDeviceResume () {
    if (!this.isWidgetLoginInProcess) {
      this.initialize();
      this.initializeForMobile();
    }
  }

  initializeForMobile () {
      this.biometricAuthStore.loadBiometricSettings();
  }

  @Watch("appUpdatePromptStore.isLoaded")
  appUpdaterLoaded (isLoaded: boolean) {
    if (isLoaded && this.canLoginWithBiometrics && !this.appUpdatePromptStore.canShowPrompt) {
      this.loginWithBiometrics();
    }
  }

  initialize () {
    this.portalCustomizationStore.setLogoFetchState(FetchState.Uninitialized);

    let partnerId = (this.$route.query.partner || this.portalCustomizationStore.partnerId);

    // mobile.partnerid is set from the bitrise build for each partner specific app
    if (Config && Config.Mobile && Config.Mobile.PartnerId && Number(Config.Mobile.PartnerId) > 0) {
      partnerId = Config.Mobile.PartnerId;
    }

    const parsedPartnerId = _.toSafeInteger(partnerId) || HsabPartnerId;
    this.themeStore.getTheme({ partnerId: parsedPartnerId, employerId: 0, forceUpdate: true });

    /* load partner logo */
    if (parsedPartnerId) {
      this.partnerContentStore.getPartnerFooterContent(parsedPartnerId);
      this.portalCustomizationStore.getPartnerCustomLogo(parsedPartnerId);
    } else {
      if (!parsedPartnerId && !this.portalCustomizationStore.isPartnerCustomizationEnabled) {
        this.portalCustomizationStore.setLogoFetchState(FetchState.Loaded);
      }
    }

    if (this.userStore.currentUser.username) {
      this.loginParam = { username: this.userStore.currentUser.username };
    }

    this.userStore.setAuthType(AuthType.Okta);
  }

  get isReady () {
    return this.portalCustomizationStore.isLoaded
      && (!isMobileApp() || this.biometricAuthStore.isBiometricAuthSettingsLoaded);
  }

  @Watch("biometricAuthStore.isBiometricAuthSettingsLoaded")
  biometricSettingsLoaded (newValue) {
    if (newValue === true) {
      this.renderOktaWidget();
    }
  }

  renderOktaWidget () {
    this.removeSignInWidget();
    this.isSubmitting = false;
    let oktaSignInConfig = {
      baseUrl: oktaConfig.employee.issuer.split("/oauth2")[0],
      clientId: oktaConfig.employee.clientId,
      username: this.loginParam.username,
      redirectUri: oktaConfig.employee.redirectUri,
      logo: this.imgSrc,
      i18n: oktaLoginResource,
      authParams: {
        issuer: oktaConfig.employee.issuer,
        scopes: oktaConfig.employee.scopes,
      },
      features: oktaConfig.features.employee,
      cookies: {
        secure: true
      },
      helpLinks: {
        forgotPassword: this.forgotPasswordLink,
        custom: [
          {
            text: "Forgot username?",
            href: this.forgotUsernameLink
          }
        ]
      },
      customButtons: []
    };

    if (this.canLoginWithBiometrics) {
      oktaSignInConfig = {
        ...oktaSignInConfig,
        customButtons: [{
          title: "Biometrics Login",
          className: "biometric-login",
          click: () => {
            this.loginWithBiometrics();
          }
        }]
      };
    }

    this.widget = new OktaSignIn(oktaSignInConfig);

    this.widget.on("ready", () => {
      if (this.isBiometricsLoginInProcess) {
        this.setUsernameAttribute()
          .then(() => document.getElementById("idp-discovery-submit").click());
      }
    });

    this.widget.on("pageRendered", context => {
      if (context.page === "idp-discovery") {
        this.reorganizeHelpLinks();
      } else if (context.page === "primary-auth") {
        const usernameElement: HTMLInputElement = document.getElementById("okta-signin-username") as any;
        const username = usernameElement.value;

        usernameElement.addEventListener("change", () => {
          this.loginParam.username = usernameElement.value;
        });

        if (username) {
          this.loginParam.username = username;
          this.pushNonOktaUserToReAuthenticate();
          this.isWidgetLoginInProcess = true;

          if (this.isBiometricsLoginInProcess) {
            /*
              This is a workaround for an issue (BEND-14096) where users are landed
              on the middle of a page instead of at top after a login. It seems like, when loggin in from javascript,
              some events are not propagating propery and pausing for few ms on the page seems to work.
            */
            pause(250)
              .then(() => this.setPasswordAttribute())
              .then(() => this.signin());
          }
        }

        this.reorganizeHelpLinks();
      }
    });

    this.widget.on("afterRender", context => this.afterRenderEvents(context));

    this.widget.showSignInToGetTokens({
      el: "#okta-signin-container",
      scopes: oktaConfig.employee.scopes
    }).then(tokens => {
      this.processTokens(tokens);
    })
      .catch(error => this.handleError({ error }));

    this.widget.on("afterError", (context, error) => this.handleError({ context, error }));
  }

  @Watch("portalCustomizationStore.isLoaded")
  onPortalCustomizationLoaded (newValue) {
    if (newValue === true) {
      if (!this.isUserLoggedIn) {
        this.renderOktaWidget();
      }
    }
  }

  processTokens (tokens: any) {
    if (Config.Mobile.AppId) {
      this.processMobileLogin(tokens);
    } else {
      this.processLogin(tokens);
    }
  }

  processMobileLogin (tokens: any) {
    mobileAppAccessCheck(Config.Mobile.AppId, this.loginParam.username)
      .then(response => {
        if (response.data.isAccessAllowed === true) {
          this.processLogin(tokens);
        } else {
          this.handleMobileAccessDenied();
        }
      });
  }

  processLogin (tokens: any) {
    this.isSubmitting = true;
    this.showPartnerFooter = false;
    OktaAuth().setTokens(tokens);
    this.userStore.setAuthType(AuthType.Okta);
    this.userStore.setLoginMethod(this.getLoginMethod());
    this.userStore.login(this.loginParam)
      .catch(error => {
        datadog.log(`HSAB login error: Unable to log in user ${this.loginParam.username}`, error, "info");
        this.logoutAndReinitialize("Unable to sign in. Please try again.");
      });
    this.trackEvent(ANALYTICS_EVENT.PortalLogin);
  }

  handleMobileAccessDenied () {
    this.clearBiometricAuthCreds();
    this.renderOktaWidget();
    this.notifierStore.setDanger("Incorrect username or password.");
    this.logMobileAppAccessDenied();
  }

  logoutAndReinitialize (message: string) {
    this.userStore.logout().then(() => {
      this.clearBiometricAuthCreds();
      this.initialize();
      this.biometricAuthStore.setFetchState(FetchState.Uninitialized);
      this.initializeForMobile();
      this.notifierStore.setDanger(message);
    });
  }

  getLoginMethod (): LoginMethod {
    if (this.isBiometricsLoginInProcess) {
      return LoginMethod.Biometrics;
    }
    return LoginMethod.DirectLogin;
  }

  get isBiometricsLoginInProcess (): boolean {
    return this.biometricAuthCreds && this.biometricAuthCreds.username && this.biometricAuthCreds.password;
  }

  get canLoginWithBiometrics (): boolean {
    return this.biometricAuthStore.biometricAuthEnabled;
  }

  get isChkboxToSetupBiometricsVisible () {
    return this.biometricAuthStore.showBiometricSetup;
  }

  styleBiometricAuthLoginButton () {
    if (!this.canLoginWithBiometrics) {
      return;
    }
    const element = document.getElementsByClassName("biometric-login")[0];
    if (element) {
      const biometricTypeDisplayValue = this.biometricAuthStore.getBiometryType.displayValue;
      element.innerHTML =
        `
        <div class="d-flex justify-content-center">
          <div>Login with ${biometricTypeDisplayValue}</div>
        </div>
        `;
    }
  }

  setUsernameAttribute () {
    return new Promise<void>((resolve, reject) => {
      const usernameElement: HTMLInputElement = document.getElementById("idp-discovery-username") as any;
      if (usernameElement) {
        usernameElement.value = this.biometricAuthCreds.username;
        usernameElement.dispatchEvent(new Event("input", { bubbles: true }));
        resolve();
        return;
      }
      reject();
    });

  }

  setPasswordAttribute () {
    return new Promise<void>((resolve, reject) => {
      const passwordElement: HTMLInputElement = document.getElementById("okta-signin-password") as any;
      if (passwordElement) {
        passwordElement.value = this.biometricAuthCreds.password;
        passwordElement.dispatchEvent(new Event("keydown", { bubbles: true }));
        resolve();
        return;
      }
      reject();
    });
  }

  clearBiometricAuthCreds () {
    this.biometricAuthCreds = null;
  }

  loginWithBiometrics () {
    this.biometricAuthStore.getLoginCreds()
      .then(creds => {
        this.biometricAuthCreds = { username: creds.username, password: creds.password };
        this.renderOktaWidget();
      })
      .catch(error => {
        this.handleBiometricLoginError(error);
      });
  }

  signin () {
    document.getElementById("okta-signin-submit").click();
  }

  afterRenderEvents (context) {
    this.bindOktaButtons();
    this.appUpdatePromptStore.initialize();
    this.UpdateWidgetWithCustomBiometricsUIElements(context);
    if (context.controller === "mfa-verify") {
      this.trackEvent(ANALYTICS_EVENT.MfaScreenView);

      if (this.isBiometricsLoginInProcess) {
        // When a user is in mfa-verify step, biometric auth creds can be cleared safely
        this.clearBiometricAuthCreds();
      }
    }
  }

  bindOktaButtons () {
    const buttons = document.querySelectorAll(
      'input[type="submit"], input[type="button"], button'
    );

    buttons.forEach(btn => {
      if (this.boundButtons.has(btn)) {
        return;
      }
      this.boundButtons.add(btn);

      btn.addEventListener("click", () => {
        const buttonLabel = this.getButtonLabel(btn);

        if (!buttonLabel) {
          return;
        }

        switch (buttonLabel.toUpperCase()) {
          case "SEND ME THE CODE":
            this.trackEvent(ANALYTICS_EVENT.MfaSendCode);
            break;
          default:
            break;
        }
      });
    });
  }

  getButtonLabel (btn) {
    if (btn.tagName === "INPUT") {
      return btn.value?.trim() || null;
    }

    if (btn.tagName === "BUTTON") {
      return btn.innerText?.trim() || null;
    }

    return null;
  }

  UpdateWidgetWithCustomBiometricsUIElements (context) {
    this.AddCheckboxForBiometricsLoginSetup(context);
    this.styleBiometricAuthLoginButton();
  }

  @Watch("biometricAuthStore.redirectToSetup")
  watchBiometricsSetup (value) {
    this.redirectToBiometricsSetup = value;
  }

  AddCheckboxForBiometricsLoginSetup (context) {
    if (!this.isChkboxToSetupBiometricsVisible) {
      return;
    }

    if (context.controller !== "idp-discovery" && context.controller !== "primary-auth") {
      return;
    }

    const checkboxContainer = document.createElement("div");
    checkboxContainer.setAttribute("class", "custom-checkbox-biometrics-setup");
    const biometricsLoginSetupCheckbox = document.createElement("input");
    biometricsLoginSetupCheckbox.setAttribute("type", "checkbox");
    biometricsLoginSetupCheckbox.setAttribute("id", "checkBoxbiometricsLogin");

    if (this.redirectToBiometricsSetup) {
      biometricsLoginSetupCheckbox.setAttribute("checked", "true");
    }

    biometricsLoginSetupCheckbox
      .addEventListener("change", () => this.biometricAuthStore.setRedirectToSetup(biometricsLoginSetupCheckbox.checked)
      );

    // set label
    const biometricsLoginSetupLabel = document.createElement("label");
    biometricsLoginSetupLabel.setAttribute("for", "checkBoxbiometricsLogin");
    biometricsLoginSetupLabel.innerHTML = `Set up ${this.biometricAuthStore.getBiometryType.displayValue}`;

    checkboxContainer.appendChild(biometricsLoginSetupCheckbox);
    checkboxContainer.appendChild(biometricsLoginSetupLabel);

    // get the parent element of rememberMe checkbox
    const container = document.getElementsByClassName("o-form-fieldset-container")[0];
    // tslint:disable-next-line:no-unused-expression
    container && container.appendChild(checkboxContainer);
  }

  handleError (params: { error: any, context?: any }) {
    this.displayUserTimeSyncMessage(params.error);

    if (!this.excludeErrorFromLog(params.error)) {
      const dataToLog = params.error;

      if (params.context) {
        Object.assign(dataToLog, params.context);
      }

      const message = dataToLog.message
        ? `Error in Okta sign-in widget: ${dataToLog.message}`
        : "Error in Okta sign-in widget";

      dataToLog.message = `${message} - Login username: ${this.loginParam.username}`;
      dataToLog.userDate = Date.now();

      datadog.log(dataToLog.message, dataToLog, "info");
    }

    this.isSubmitting = false;
    this.showPartnerFooter = true;

    if (this.isBiometricsLoginInProcess) {
      this.handleBiometricLoginError();
    }
  }

  displayUserTimeSyncMessage (error: any) {
    if (error.name !== "OAUTH_ERROR" || typeof error.message !== "string") {
      return;
    }

    const message = error.message.toLowerCase();

    if (jwtErrors.some(e => e.toLowerCase() === message)) {
      window.location.href = "#";
      this.notifierStore.setDanger(
        "Unable to sign in. Please ensure that your device's time is in sync, then try again.");
    }
  }

  excludeErrorFromLog (error: any) {
    const errorCode = _.get(error, "xhr.responseJSON.errorCode", "");
    return oktaErrorCodesToExcludeFromLog.includes(errorCode);
  }

  handleBiometricLoginError (error?) {
    let errorMessage = ERROR_MESSAGE_BIOMETRIC_LOGIN_FAILED;
    if (error && error.message) {
      errorMessage = error.message;
    }

    datadog.log("Biometrics login failed. Unable to retrieve credentials from the device.",  error, "info");

    this.clearBiometricAuthCreds();
    this.biometricAuthStore.disableBiometricAuth()
      .then(() => {
        this.notifierStore.setInfo(errorMessage);
        this.biometricAuthStore.loadBiometricSettings();
      });
  }

  reorganizeHelpLinks () {
    const needHelpSigningIn = document.getElementsByClassName("link help js-help")[0];
    if (needHelpSigningIn) {
      needHelpSigningIn.setAttribute("style", "display: none;");
    }

    const helpLinksContainer = document.getElementById("help-links-container");
    if (helpLinksContainer) {
      helpLinksContainer.removeAttribute("style");
    }

    const helpLink = document.getElementsByClassName("link js-help-link")[0];
    if (helpLink) {
      helpLink.parentElement.remove();
    }

    const backToSignInLink = document.getElementsByClassName("auth-footer footer-back-link")[0];
    if (backToSignInLink) {
      backToSignInLink.innerHTML = '<a href="#" class="link help" data-se="back-link">Back</a>';
    }

    this.showPartnerFooter = true;
  }

  logMobileAppAccessDenied () {
    const data = {
      loginUsername: this.loginParam.username,
      mobileInfo: Config.Mobile,
      userDate: Date.now()
    };

    datadog.log("Mobile app access denied", data, "warn");
  }

  trackEvent (event: string) {
    Vue.prototype.$gtm?.trackEvent({ event });
  }

  destroyed () {
    // Remove the widget from the DOM on path change
    this.removeSignInWidget();
    // Remove event listeners
    document.removeEventListener("deviceready", this.initializeForMobile);
    document.removeEventListener("resume", this.onMobileDeviceResume);
  }

  removeSignInWidget () {
    if (this.widget) {
      this.widget.off();
      this.widget.remove();
    }
  }
}
